import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
export interface User {
  id: string;
  name: string;
  email: string;
  picture?: string;
  email_verified?: boolean;
}

export interface Research {
  id: string;
  title: string;
  query: string;
  status: 'active' | 'completed' | 'failed';
  progress: number;
  createdAt: string;
  updatedAt: string;
  research_length?: 'quick_scan' | 'targeted' | 'deep_dive';
}

export interface Template {
  id: string;
  template_name: string;
  description: string;
  initial_query: string;
  research_length: string;
  created_at: string;
  updated_at: string;
  topic_count: number;
  question_count: number;
  usage_status: 'used' | 'unused';
  execution_count: number;
}

interface AppState {
  // User state
  user: User | null;
  apiKey: string;
  isKeyRequired: boolean;
  
  // Theme state
  theme: 'light' | 'dark' | 'system';
  
  // Sidebar state
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  
  // Research state
  researches: Research[];
  activeResearch: Research | null;
  
  // Templates state
  templates: Template[];
  activeTemplate: Template | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setApiKey: (key: string) => void;
  setIsKeyRequired: (required: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  setResearches: (researches: Research[]) => void;
  addResearch: (research: Research) => void;
  updateResearch: (id: string, updates: Partial<Research>) => void;
  setActiveResearch: (research: Research | null) => void;
  setTemplates: (templates: Template[]) => void;
  addTemplate: (template: Template) => void;
  updateTemplate: (id: string, updates: Partial<Template>) => void;
  deleteTemplate: (id: string) => void;
  setActiveTemplate: (template: Template | null) => void;
  clearApiKey: () => void;
  reset: () => void;
}

const initialState = {
  user: null,
  apiKey: '',
  isKeyRequired: true,
  theme: 'light' as const,
  sidebarCollapsed: true,
  mobileSidebarOpen: false,
  researches: [],
  activeResearch: null,
  templates: [],
  activeTemplate: null,
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      ...initialState,
      
      // User actions
      setUser: (user) => set({ user }),
      setApiKey: (apiKey) => set({ apiKey }),
      setIsKeyRequired: (isKeyRequired) => set({ isKeyRequired }),
      
      // Theme actions
      setTheme: (theme) => set({ theme }),
      
      // Sidebar actions
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
      
      // Research actions
      setResearches: (researches) => set({ researches }),
      addResearch: (research) => set((state) => ({ 
        researches: [research, ...state.researches] 
      })),
      updateResearch: (id, updates) => set((state) => ({
        researches: state.researches.map((r) => 
          r.id === id ? { ...r, ...updates } : r
        ),
        activeResearch: state.activeResearch?.id === id 
          ? { ...state.activeResearch, ...updates }
          : state.activeResearch
      })),
      setActiveResearch: (activeResearch) => set({ activeResearch }),
      
      // Template actions
      setTemplates: (templates) => set({ templates }),
      addTemplate: (template) => set((state) => ({ 
        templates: [template, ...state.templates] 
      })),
      updateTemplate: (id, updates) => set((state) => ({
        templates: state.templates.map((t) => 
          t.id === id ? { ...t, ...updates } : t
        ),
        activeTemplate: state.activeTemplate?.id === id 
          ? { ...state.activeTemplate, ...updates }
          : state.activeTemplate
      })),
      deleteTemplate: (id) => set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        activeTemplate: state.activeTemplate?.id === id ? null : state.activeTemplate
      })),
      setActiveTemplate: (activeTemplate) => set({ activeTemplate }),
      
      // Utility actions
      clearApiKey: () => set({ apiKey: '' }),
      reset: () => set(initialState),
    }),
    {
      name: 'deep-research-storage',
      partialize: (state) => ({
        // Only persist certain values
        // NOTE: API key is NOT persisted for security reasons
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

// Selectors
export const selectUser = (state: AppState) => state.user;
export const selectApiKey = (state: AppState) => state.apiKey;
export const selectTheme = (state: AppState) => state.theme;
export const selectSidebarCollapsed = (state: AppState) => state.sidebarCollapsed;
export const selectMobileSidebarOpen = (state: AppState) => state.mobileSidebarOpen;
export const selectResearches = (state: AppState) => state.researches;
export const selectActiveResearch = (state: AppState) => state.activeResearch;
export const selectTemplates = (state: AppState) => state.templates;
export const selectActiveTemplate = (state: AppState) => state.activeTemplate;